import styled, { StyledComponent } from '@emotion/styled'
import { withClass } from 'common/emotion'

import { breakpoint, calcRem, breakpointMax } from 'component-library/__styling/style-functions'
import { metrics, colors, easing, fonts } from 'component-library/__styling/settings'

import { TheWestLogo } from 'component-library/logos/TheWestLogo/TheWestLogo'
import { Timestamp } from 'component-library/typography/Timestamp/Timestamp'
import { WebLink, WebLinkProps } from 'common/navigation/web-link'
import { IconChevronRight } from 'component-library/icons/IconChevronRight/IconChevronRight'
import { IconSubscribe } from 'component-library/icons/IconSubscribe/IconSubscribe'
import { IconMyNews } from 'component-library/icons/IconMyNews/IconMyNews'
import { AmpImage } from 'component-library/content/Picture/Picture.amp'
import { conditionalStyles } from 'component-library/__styling/conditional-styles'
import { filterComponent } from 'component-library/__helpers/filter-component'
import { LogoProps } from './logos/createHeaderLogo'

export const styledTheWestClassName = 'TheWest-Logo' as any
const styledSubscribeLinkClassName = 'SubscribeLink'

export const StyledTheWestHeaderWrapper = styled('div')({
    display: 'block',
    width: '100%',
    maxWidth: metrics.thewest.mainContentWidth,
    padding: `0 ${calcRem(15)}`,
    margin: '0 auto',
})

export const StyledTheWestHeader = styled('div')<{ showDivider: boolean }>(
    ({ theme, showDivider }) => ({
        width: '100%',
        padding: `${
            theme.componentMetrics.mastheadOffset ? theme.componentMetrics.mastheadOffset : '1.9'
        }% 0`,
        display: 'flex',
        position: 'relative',
        alignItems: 'center',
        flexWrap: 'wrap',
        justifyContent: 'center',
        // Show a divider if requested, mainly for when the pageType is a feature-publication and we dont have the cricket ball feature toggled on
        borderBottom: showDivider ? `solid 1px ${colors.thewest.greyDolphin}` : undefined,
    })
)
export const StyledTheWestLogo = styled(TheWestLogo)(styledTheWestClassName, {
    display: 'block',
    padding: calcRem(metrics.thewest.margins.md),
})

export interface ImageLogoProps {
    /* This allows amp to get the aspect ratio */
    ampInitialSize: { width: number; height: number }
    heights: {
        desktop: number
        tablet: number
        mobile: number
    }
    verticalSpacing?: number
}

export const StyledImageLogo = styled('img')<LogoProps>(props => ({
    display: 'block',
    height: calcRem(props.heights.desktop),
    margin: '0 auto',
    paddingTop: props.verticalSpacing ? calcRem(props.verticalSpacing) : 0,
    paddingBottom: props.verticalSpacing ? calcRem(props.verticalSpacing) : 0,
    maxWidth: '100%',
    [breakpointMax('sm')]: {
        height: calcRem(props.heights.tablet),
    },
    [breakpointMax('xxs')]: {
        height: calcRem(props.heights.mobile),
    },
}))

export const StyledAmpLogo = styled(AmpImage)({
    display: 'block',
})

export const StyledTimestampWrap = styled('div')({
    position: 'absolute',
    right: 0,
    top: '50%',
    transform: 'translateY(-50%)',
    textAlign: 'right',
})

export const StyledHeaderTimestamp = styled(Timestamp)({
    display: 'none',

    [breakpoint('md')]: {
        display: 'block',
        alignContent: 'center',
        fontFamily: fonts.thewest.sansSerif,
        fontSize: calcRem(14),
    },
})

export const StyledInHouseLinks = styled('nav')({
    display: 'none',

    [breakpoint('md')]: {
        display: 'flex',
        justifyContent: 'flex-end',
        flexWrap: 'nowrap',
        alignItems: 'center',
        marginBottom: calcRem(metrics.thewest.margins.md),
    },
})

export const StyledLink = styled(WebLink)(({ theme }) => ({
    paddingLeft: calcRem(metrics.thewest.margins.sm),
    lineHeight: 1,
    fontFamily: fonts.thewest.sansSerif,
    fontSize: calcRem(14),
    fontWeight: 500,
    color: colors.black,
    textDecoration: 'none',
    transition: `color ${easing.easeOut.fast}`,

    '&:not(:last-child)': {
        borderRight: `1px solid ${colors.black}`,
        paddingRight: calcRem(metrics.thewest.margins.sm),
    },

    '&:hover, &:focus': {
        color: theme.sectionValues.primaryColor,
        textDecoration: 'underline',
    },

    '&:focus': {
        outline: 'none',
    },
}))

interface StyledSubscribeLinkProps extends WebLinkProps {
    shouldShrink?: boolean
}

const FilteredSubscribeLink = filterComponent<StyledSubscribeLinkProps, string>(WebLink, [
    'shouldShrink',
])

export const StyledSubscribeLink = styled(FilteredSubscribeLink)(
    ({ theme }) => ({
        display: 'none',

        [breakpoint('md')]: {
            display: 'block',
            position: 'absolute',
            left: 0,
            top: '50%',
            transform: 'translateY(-50%)',

            color: theme.sectionValues.primaryColor,
            fontFamily: fonts.thewest.sansSerif,
            fontSize: calcRem(15),
            fontWeight: 700,
            textTransform: 'uppercase',
            textDecoration: 'none',

            background: 'none',
            border: 'none',
            cursor: 'pointer',

            '&:hover': {
                color: colors.thewest.greyGorilla,

                '.icon-chevron-right': {
                    fill: colors.thewest.greyGorilla,
                },
            },

            '&:focus': {
                color: colors.thewest.greyKoala,
                textDecoration: 'underline',
                outline: 'none',

                '.icon-chevron-right': {
                    fill: colors.thewest.greyKoala,
                },
            },
        },
    }),
    props => ({
        [breakpoint('md')]: {
            ...conditionalStyles(props.shouldShrink, {
                fontSize: calcRem(12),
                textAlign: 'center',

                [`& > .${styledSubscribeLinkClassName}`]: {
                    height: calcRem(36),
                },
            }),
        },
    })
)

export const StyledSubscribeButton = StyledSubscribeLink.withComponent('button') as StyledComponent<
    JSX.IntrinsicElements['button'],
    { shouldShrink?: boolean }
>

export const StyledChevronRight = styled(IconChevronRight)(({ theme }) => ({
    width: calcRem(8),
    height: calcRem(11),
    fill: theme.sectionValues.primaryColor,
}))

export const StyledSubscribeIcon = withClass(styledSubscribeLinkClassName)(
    styled(IconSubscribe)({
        display: 'block',
        width: calcRem(88),
        height: calcRem(51),
        marginBottom: metrics.thewest.margins.xs,
    })
)

export const StyledMyNewsIcon = styled(IconMyNews)({
    display: 'block',
    width: calcRem(88),
    height: calcRem(51),
    marginBottom: metrics.thewest.margins.xs,
})
